import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, FIRM } from "../data/content";

export default function Navbar({ onConsult }) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`relative transition-all duration-500 ${scrolled ? "nav-glass border-b border-[rgba(11,18,32,0.06)]" : "bg-transparent border-b border-transparent"}`}
    >
      <div className="container-x flex items-center justify-between h-[80px]">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 sm:gap-4 group">
          <span
            data-testid="brand-logo-chip"
            className="inline-flex items-center justify-center bg-white rounded-md px-2 py-1 shadow-sm ring-1 ring-black/5 flex-shrink-0"
          >
            <img
              src="/ca-india-logo.png"
              alt="CA India — Institute of Chartered Accountants of India"
              className="h-7 sm:h-8 w-auto block"
              loading="eager"
              decoding="async"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className={`font-serif text-[1.05rem] whitespace-nowrap transition-colors ${scrolled ? "text-[#0A192F]" : "text-white"}`}>CA. Nitta Ravi <span className="text-[#C5A059] italic">Kishore</span></span>
            <span className={`overline text-[9px] whitespace-nowrap transition-colors ${scrolled ? "text-[#64748B]" : "text-white/55"}`}>R. K. Nitta &amp; Co.</span>
          </span>
          <span className={`sm:hidden font-serif text-base whitespace-nowrap transition-colors ${scrolled ? "text-[#0A192F]" : "text-white"}`}>CA. Nitta Ravi Kishore</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s|&/g, "-")}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `text-[0.85rem] tracking-wide transition-colors link-underline whitespace-nowrap ${
                  scrolled
                    ? (isActive ? "text-[#0A192F]" : "text-[#475569] hover:text-[#0A192F]")
                    : (isActive ? "text-white" : "text-white/70 hover:text-white")
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-consult-button"
            className={`hidden md:inline-flex items-center gap-2 text-[0.82rem] py-2.5 px-4 transition-all whitespace-nowrap ${
              scrolled
                ? "bg-[#0A192F] text-white border border-[#0A192F] hover:bg-[#C5A059] hover:border-[#C5A059] hover:text-[#0A192F]"
                : "bg-[#C5A059] text-[#0A192F] border border-[#C5A059] hover:bg-white hover:border-white"
            }`}
          >
            Get In Touch <ArrowUpRight size={14} />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 transition-colors ${scrolled ? "text-[#0A192F]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[rgba(11,18,32,0.08)] bg-white">
          <div className="container-x py-8 flex flex-col">
            {NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center justify-between py-4 text-lg font-serif border-b border-[rgba(11,18,32,0.08)] ${isActive ? "text-[#0A192F]" : "text-[#0A192F]/70"}`
                }
              >
                <span><span className="num-tag mr-3">{String(i+1).padStart(2,"0")}</span>{item.label}</span>
                <ArrowUpRight size={16} className="text-[#C5A059]" />
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-consult-button"
              className="btn-gold mt-8 justify-center"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
